import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Button,
    Skeleton,
} from "@heroui/react";
import { LuArrowLeft, LuPencil, LuCopy } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { useUser } from "@/context/UserContext";
import { formatDate, formatPhoneNumber, copyToClipboard } from "@/utils";

const SupplierDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const { data, isLoading, error, refetch } = useFetchData(
        `suppliers/${id}/show`,
    );

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Supplier yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/suppliers")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Supplier"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const supplier = data;
    const addresses = supplier?.addresses || [];
    const contacts = supplier?.contact_numbers || [];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Supplier"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Supplier", href: "/suppliers" },
                        isLoading
                            ? { label: "..." }
                            : { label: supplier?.name },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/suppliers")}
                    >
                        Kembali
                    </Button>
                    {hasPermission("supplier.edit") && (
                        <Button
                            as={Link}
                            to={`/suppliers/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Informasi Supplier */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Supplier
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <InfoItem
                                label="Nama Supplier"
                                value={supplier?.name}
                            />
                            <div className="flex flex-col gap-1">
                                <ThemedText
                                    size="xs"
                                    variant="secondary"
                                    className="font-medium"
                                >
                                    Status Keaktifan
                                </ThemedText>
                                <div>
                                    <Chip
                                        color={
                                            supplier?.active_status
                                                ?.badge_color || "default"
                                        }
                                        size="sm"
                                        variant="flat"
                                        className="font-medium"
                                    >
                                        {supplier?.active_status?.name ||
                                            "Unknown"}
                                    </Chip>
                                </div>
                            </div>
                            {supplier?.products_summary && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <InfoItem
                                        label="Produk yang Dijual"
                                        value={supplier.products_summary}
                                    />
                                </div>
                            )}
                            {supplier?.description && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                    <InfoItem
                                        label="Keterangan"
                                        value={supplier.description}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Alamat */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Alamat
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {addresses.length} alamat
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-4">
                            {[...Array(1)].map((_, i) => (
                                <div
                                    key={i}
                                    className="border-default-200 rounded-lg border p-4"
                                >
                                    <Skeleton className="mb-3 h-3 w-16 rounded-lg" />
                                    <div className="mb-3">
                                        <Skeleton className="mb-1 h-3 w-20 rounded-lg" />
                                        <Skeleton className="h-4 w-full max-w-md rounded-lg" />
                                    </div>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                        {[...Array(4)].map((_, j) => (
                                            <div key={j} className="flex flex-col gap-1">
                                                <Skeleton className="h-3 w-20 rounded-lg" />
                                                <Skeleton className="h-4 w-32 rounded-lg" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : addresses.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Belum ada data alamat.
                        </ThemedText>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {addresses.map((addr, index) => (
                                <div
                                    key={addr.id}
                                    className="border-default-200 rounded-lg border p-4"
                                >
                                    {addresses.length > 1 && (
                                        <ThemedText
                                            size="xs"
                                            variant="caption"
                                            className="mb-2 font-medium"
                                        >
                                            Alamat {index + 1}
                                        </ThemedText>
                                    )}
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                        {addr.full_address && (
                                            <div className="sm:col-span-2 lg:col-span-3">
                                                <CopyableInfo
                                                    label="Alamat Lengkap"
                                                    value={addr.full_address}
                                                    copyLabel="Alamat"
                                                />
                                            </div>
                                        )}
                                        {(addr.neighborhood_unit ||
                                            addr.sub_neighborhood_unit) && (
                                            <InfoItem
                                                label="RT / RW"
                                                value={`${addr.neighborhood_unit || "-"} / ${addr.sub_neighborhood_unit || "-"}`}
                                            />
                                        )}
                                        {addr.village && (
                                            <InfoItem
                                                label="Kelurahan"
                                                value={addr.village}
                                            />
                                        )}
                                        {addr.district && (
                                            <InfoItem
                                                label="Kecamatan"
                                                value={addr.district}
                                            />
                                        )}
                                        {addr.city && (
                                            <InfoItem
                                                label="Kota"
                                                value={addr.city}
                                            />
                                        )}
                                        {addr.province && (
                                            <InfoItem
                                                label="Provinsi"
                                                value={addr.province}
                                            />
                                        )}
                                        {addr.postal_code && (
                                            <InfoItem
                                                label="Kode Pos"
                                                value={addr.postal_code}
                                            />
                                        )}
                                    </div>
                                    {addr.google_map_url && (
                                        <div className="mt-3">
                                            <a
                                                href={addr.google_map_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary text-xs hover:underline"
                                            >
                                                Lihat di Google Maps
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Kontak */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Kontak
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {contacts.length} kontak
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(2)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : contacts.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Belum ada data kontak.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Tipe</th>
                                    <th>Nomor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map((contact, index) => (
                                    <tr key={contact.id}>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {index + 1}.
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {contact.contact_type?.name ||
                                                    "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <span className="inline-flex items-center gap-1">
                                                <ThemedText size="xs">
                                                    {formatPhoneNumber(
                                                        contact.number,
                                                    )}
                                                </ThemedText>
                                                <LuCopy
                                                    size={12}
                                                    className="text-default-400 hover:text-default-600 shrink-0 cursor-pointer"
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            contact.number,
                                                            "Nomor telepon",
                                                        )
                                                    }
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Footer Metadata */}
            {!isLoading && supplier && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
                    <ThemedText size="xs" variant="secondary">
                        Dibuat:{" "}
                        {formatDate(supplier.created_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        ·
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        Terakhir diubah:{" "}
                        {formatDate(supplier.updated_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                </div>
            )}
        </div>
    );
};

const InfoItem = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm">{value}</ThemedText>
    </div>
);

const CopyableInfo = ({ label, value, copyLabel }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <span className="inline-flex items-center gap-1.5">
            <ThemedText size="sm">{value}</ThemedText>
            <LuCopy
                size={12}
                className="text-default-400 hover:text-default-600 shrink-0 cursor-pointer"
                onClick={() => copyToClipboard(value, copyLabel || label)}
            />
        </span>
    </div>
);

export default SupplierDetail;
